import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../../api/axios';
import { formatDate } from '../../utils/dateUtils';

const PatientInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [statusFilter, setStatusFilter] = useState('');

  const fetchInvoices = async (page = 1, status = '') => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (status) params.status = status;
      const res = await API.get('/invoices', { params });
      setInvoices(res.data.data.invoices);
      setPagination({
        page: res.data.data.pagination.page,
        totalPages: res.data.data.pagination.totalPages,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(1, statusFilter);
  }, [statusFilter]);

  const getStatusBadge = (status) => {
    const map = {
      pending: 'En attente',
      partial: 'Partiel',
      paid: 'Payée',
      overdue: 'En retard',
      cancelled: 'Annulée',
    };
    return <span className={`badge badge-${status}`}>{map[status] || status}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 600 }}>Mes factures</h2>
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Consultez et payez vos factures médicales</p>
        </div>
        <div>
          <select
            className="form-select"
            style={{ width: 180 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="partial">Partiel</option>
            <option value="paid">Payée</option>
            <option value="overdue">En retard</option>
            <option value="cancelled">Annulée</option>
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-page"><div className="spinner"></div></div>
        ) : invoices.length === 0 ? (
          <div className="empty-state">
            <h3>Aucune facture trouvée</h3>
            <p>Aucune facture ne correspond à votre recherche.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>N° Facture</th>
                    <th>Motif</th>
                    <th>Montant</th>
                    <th>Payé</th>
                    <th>Statut</th>
                    <th>Échéance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td style={{ fontWeight: 600 }}>{inv.invoice_number}</td>
                      <td>{inv.motif}</td>
                      <td style={{ fontWeight: 600 }}>
                        {parseFloat(inv.amount).toLocaleString()} {inv.currency}
                      </td>
                      <td>{parseFloat(inv.amount_paid).toLocaleString()} {inv.currency}</td>
                      <td>{getStatusBadge(inv.status)}</td>
                      <td>{formatDate(inv.due_date)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Link to={`/patient/invoices/${inv.id}`} className="btn btn-outline btn-sm">
                            Détails
                          </Link>
                          {(inv.status === 'pending' || inv.status === 'partial' || inv.status === 'overdue') && (
                            <Link to={`/patient/pay/${inv.id}`} className="btn btn-primary btn-sm">
                              Payer
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => fetchInvoices(pagination.page - 1, statusFilter)}
                >
                  Précédent
                </button>
                <span style={{ fontSize: 14, color: 'var(--gray-600)' }}>
                  Page {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchInvoices(pagination.page + 1, statusFilter)}
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PatientInvoices;
